/**
 * Supabase Client Singleton — Web/App 공통 SSoT
 *
 * `app/`·`web/` 워크스페이스가 본 모듈을 통해 SupabaseClient 인스턴스를 한 번만
 * 생성하도록 강제한다. 두 번 호출돼도 동일 인스턴스 반환. (`mcp/`는 Service Role Key
 * 기반 서버 컨텍스트로 별도 `createSupabaseAdapter` 사용)
 *
 * 배경 (자세한 분석은 memory/web-app-supabase-singleton.md):
 *   web/ 번들이 alias로 app 페이지를 재사용할 때 `app/src/services/supabase.ts`와
 *   `web/src/lib/supabase.ts`가 각각 createClient를 호출 → 같은 storageKey를 가진
 *   두 GoTrueClient 인스턴스 → PKCE OAuth callback에서 verifier race 발생
 *   (`AuthPKCECodeVerifierMissingError`).
 *
 * 옵션 통일 (community/공식 best practice 합치):
 *   - flowType: 'pkce' — SPA + native 양쪽 안전
 *   - detectSessionInUrl: false — /auth/callback 페이지에서 명시적 exchangeCodeForSession 호출.
 *     supabase-js#931에서 보고된 PKCE+auto-detect race를 정면 차단.
 *   - storageKey 환경별 분리 — alias 경유로 두 wrapper가 호출돼도 storage cross-write 차단.
 *   - persistSession / autoRefreshToken — 동일 (양쪽 동일 동작 유지).
 *
 * 사용 패턴:
 *   - `app/src/services/supabase.ts` — Capacitor 환경 감지 + Preferences storage 주입
 *   - `web/src/lib/supabase.ts` — 그냥 호출 (platform='web')
 *   - 두 wrapper 모두 본 singleton 재export. 추가 createClient 호출 금지.
 */

import {
  createClient,
  type SupabaseClient,
  type SupabaseClientOptions,
} from "@supabase/supabase-js";

type AuthOptions = NonNullable<SupabaseClientOptions<"public">["auth"]>;
type StorageAdapter = NonNullable<AuthOptions["storage"]>;

let _instance: SupabaseClient | null = null;

export interface GetSupabaseClientArgs {
  /** Supabase 프로젝트 URL */
  url: string;
  /** Supabase anon key (Service Role Key는 사용 금지 — 클라이언트 번들에 포함됨) */
  key: string;
  /**
   * 실행 환경. storageKey 네임스페이스 분리에 사용.
   *   - 'web': 브라우저 환경 (Vite SPA). storageKey = 'tldr-web-auth'.
   *   - 'native': Capacitor 환경 (iOS/Android). storageKey = 'tldr-native-auth'.
   * 두 인스턴스가 같은 도메인에서 공존할 수 없더라도 명시적 분리로 방어.
   */
  platform: "web" | "native";
  /**
   * 선택적 storage adapter. native 환경에서 Capacitor Preferences 주입용.
   * 미지정 시 supabase-js 기본값(브라우저 localStorage) 사용.
   */
  storage?: StorageAdapter;
}

/**
 * SupabaseClient singleton 반환. 두 번째 이후 호출은 첫 호출의 인스턴스 그대로 반환.
 *
 * @example
 * // app/src/services/supabase.ts
 * import { Capacitor } from '@capacitor/core';
 * import { Preferences } from '@capacitor/preferences';
 * import { getSupabaseClient } from '@tldr/core';
 *
 * const isNative = Capacitor.isNativePlatform();
 * export const supabase = getSupabaseClient({
 *   url: import.meta.env.VITE_SUPABASE_URL,
 *   key: import.meta.env.VITE_SUPABASE_ANON_KEY,
 *   platform: isNative ? 'native' : 'web',
 *   storage: isNative ? capacitorPreferencesStorage() : undefined,
 * });
 */
export function getSupabaseClient(args: GetSupabaseClientArgs): SupabaseClient {
  if (_instance) return _instance;

  const storageKey =
    args.platform === "native" ? "tldr-native-auth" : "tldr-web-auth";

  _instance = createClient(args.url, args.key, {
    auth: {
      flowType: "pkce",
      detectSessionInUrl: false,
      persistSession: true,
      autoRefreshToken: true,
      storageKey,
      storage: args.storage,
    },
  });

  return _instance;
}

/**
 * 테스트 전용 — singleton 캐시 리셋.
 * 프로덕션 코드에서 사용 금지.
 */
export function __resetSupabaseClientForTests(): void {
  _instance = null;
}
